import React, { useEffect, useState } from "react";
import api, { assetUrl } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Upload, Trash2, FolderOpen, Download } from "lucide-react";

const TYPES = [
    { v: "legal",          l: "Legales" },
    { v: "maps",           l: "Mapas" },
    { v: "products",       l: "Productos" },
    { v: "certifications", l: "Certificaciones" },
    { v: "constancias",    l: "Constancias" },
    // Legacy (kept only so existing docs still display their type; hidden from the upload dropdown)
    { v: "map_traps",      l: "Mapa de trampas (legado)", legacy: true },
    { v: "map_bait_stations", l: "Mapa de cebaderos (legado)", legacy: true },
    { v: "certificate",    l: "Constancia (legado)", legacy: true },
    { v: "other",          l: "Otro (legado)", legacy: true },
];

const UPLOAD_TYPES = TYPES.filter((t) => !t.legacy);

export default function AdminDocuments() {
    const [clients, setClients] = useState([]);
    const [docs, setDocs] = useState([]);
    const [client, setClient] = useState("");
    const [type, setType] = useState("legal");
    const [title, setTitle] = useState("");
    const [file, setFile] = useState(null);
    const [uploading, setUploading] = useState(false);

    useEffect(() => { api.get("/clients").then((r) => { setClients(r.data); if (r.data[0]) setClient(r.data[0].id); }); }, []);
    const load = () => {
        const params = client ? { client_id: client } : {};
        api.get("/documents", { params }).then((r) => setDocs(r.data));
    };
    useEffect(() => { if (client) load(); }, [client]);

    const upload = async () => {
        if (!file || !title || !client) return toast.error("Complete los campos y seleccione archivo");
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("client_id", client);
            fd.append("document_type", type);
            fd.append("title", title);
            fd.append("file", file);
            await api.post("/documents", fd, { headers: { "Content-Type": "multipart/form-data" } });
            toast.success("Documento subido"); setTitle(""); setFile(null); load();
        } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
        finally { setUploading(false); }
    };

    const remove = async (id) => {
        if (!window.confirm("Eliminar documento?")) return;
        await api.delete(`/documents/${id}`); toast.success("Eliminado"); load();
    };

    return (
        <div data-testid="admin-documents">
            <h1 className="text-2xl font-semibold mb-4 flex items-center gap-2" style={{ fontFamily: "'Space Grotesk',sans-serif" }}><FolderOpen className="w-6 h-6" /> Documentos por cliente</h1>
            <Card className="p-4 mb-4">
                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-3">
                    <div>
                        <Label>Cliente</Label>
                        <Select value={client} onValueChange={setClient}>
                            <SelectTrigger data-testid="docs-client-select"><SelectValue /></SelectTrigger>
                            <SelectContent>{clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label>Tipo</Label>
                        <Select value={type} onValueChange={setType}>
                            <SelectTrigger data-testid="docs-type-select"><SelectValue /></SelectTrigger>
                            <SelectContent>{UPLOAD_TYPES.map((t) => <SelectItem key={t.v} value={t.v}>{t.l}</SelectItem>)}</SelectContent>
                        </Select>
                    </div>
                    <div><Label>Título</Label><Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Mapa bodega A" data-testid="docs-title" /></div>
                    <div><Label>Archivo (PDF)</Label><Input type="file" accept=".pdf,.png,.jpg,.jpeg" onChange={(e) => setFile(e.target.files?.[0])} data-testid="docs-file" /></div>
                </div>
                <Button onClick={upload} disabled={uploading} className="bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] hover:bg-[hsl(var(--accent))]/90" data-testid="docs-upload-button">
                    <Upload className="w-4 h-4 mr-1" /> {uploading ? "Subiendo..." : "Subir documento"}
                </Button>
            </Card>
            <Card>
                <Table>
                    <TableHeader><TableRow><TableHead>Título</TableHead><TableHead>Tipo</TableHead><TableHead>Fecha</TableHead><TableHead className="text-right">Acciones</TableHead></TableRow></TableHeader>
                    <TableBody>
                        {docs.length === 0 && <TableRow><TableCell colSpan={4} className="text-center py-8 text-muted-foreground">Sin documentos para este cliente</TableCell></TableRow>}
                        {docs.map((d) => (
                            <TableRow key={d.id}>
                                <TableCell className="font-medium">{d.title}</TableCell>
                                <TableCell>{TYPES.find((t) => t.v === d.document_type)?.l}</TableCell>
                                <TableCell className="font-mono text-sm">{d.created_at?.slice(0, 10)}</TableCell>
                                <TableCell className="text-right space-x-1">
                                    <a href={assetUrl(d.file_url)} target="_blank" rel="noreferrer"><Button variant="ghost" size="icon"><Download className="w-4 h-4" /></Button></a>
                                    <Button variant="ghost" size="icon" onClick={() => remove(d.id)}><Trash2 className="w-4 h-4 text-red-600" /></Button>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </Card>
        </div>
    );
}
